import { addDays, diffDays, hmToMin, isoDow, minToHM, nthOfMonth, startOfWeek, today } from './dates.js';
import { S, persist, uid } from './state.js';
import { t } from './i18n/index.js';

export const lessonLabel=s=>(s&&s.subject)?t('inv.lessonsIn',{subject:s.subject}):t('inv.hoursOf');

export const stu=id=>S.students.find(s=>s.id===id);
/** Online link of a lesson: its own link, else the student's link (only when the lesson is online). */
export const meetOf=e=>{if(!e)return '';const s=stu(e.studentId);if(placeOf(e,s)!=='online')return '';return ((e.meet)||(s||{}).meet||'').trim()};   // in-person lessons have no link
export const stuName=id=>{const s=stu(id);return s?(s.first+' '+(s.last||'')).trim():'?'};
export const stuInitials=s=>((s.first[0]||'')+(s.last?s.last[0]:'')).toUpperCase();
/* ---------- where a lesson happens, and what it costs there ----------
   A teacher on their own: 'site' (vor Ort, at the student's) or 'online'.
   A school: 'school' (in der Schule), 'site' (beim Schüler) or 'online'.
   The first place of the list is the main one: it uses the plain rate (settings.defRate, student.rate);
   the others have their own rates (rateSite, rateOnline) and fall back to the main one when empty. */
let schoolCtx=()=>false;
export function setSchoolCtx(f){schoolCtx=f}
export const isSchoolStu=s=>!!(s&&s._org)||!!schoolCtx();
export const placesFor=s=>isSchoolStu(s)?['school','site','online']:['site','online'];
export const mainPlace=s=>placesFor(s)[0];
const okPlace=(p,s)=>!!p&&placesFor(s).includes(p);
/** the student's usual place: set on the student, else online when they have a link */
export function stuPlace(s){if(s&&okPlace(s.place,s))return s.place;return s&&(s.meet||'').trim()?'online':mainPlace(s)}
/** where a lesson happens: its own place, else online when it has a link, else the student's usual place */
export function placeOf(e,s){s=s||(e&&stu(e.studentId));if(e&&okPlace(e.place,s))return e.place;if(e&&(e.meet||'').trim())return 'online';return stuPlace(s)}
export function placeLabel(p,s){return t(p==='site'&&isSchoolStu(s)?'place.home':'place.'+p)}
export const RATE_KEY={online:'rateOnline',site:'rateSite',school:'rate'};
const num=(o,k)=>{const v=o&&o[k];return v!=null&&v!==''&&isFinite(+v)?+v:null};
/** the default rate at a place: settings first, then the main rate */
export function placeRate(p,s){const st=S.settings||{};
  if(p===mainPlace(s))return num(st,'defRate')??0;
  return num(st,p==='online'?'rateOnline':'rateSite')??num(st,'defRate')??0}
/** true when the teacher or school charges differently by place (then invoices name the place) */
export const placesPriced=()=>{const st=S.settings||{};return num(st,'rateOnline')!=null||num(st,'rateSite')!=null||(S.students||[]).some(x=>num(x,'rateOnline')!=null||num(x,'rateSite')!=null)};
/** ', Online' on an invoice line, only when prices differ by place */
export const placeTag=(e,s)=>placesPriced()?', '+placeLabel(placeOf(e,s),s):'';
export function stuRate(s,ses){if(ses&&ses.rate!=null&&ses.rate!=='')return +ses.rate;
  const p=ses?placeOf(ses,s):stuPlace(s);
  if(p===mainPlace(s))return num(s,'rate')??placeRate(p,s);
  const k=p==='online'?'rateOnline':'rateSite';
  return num(s,k)??num(S.settings,k)??num(s,'rate')??placeRate(mainPlace(s),s)}

/* ---------- sessions & recurrence engine ----------
   session: {id,studentId,date,time,dur,status,recId?,rate?,notes,summary?,billed?,invoiceId?,pkgId?,billable?,meet?,place?}
   rec: {id,studentId,time,dur,freq:'weekly'|'biweekly'|'every4'|'monthly',days:[1..7],start,until?,meet?,place?,ended?}
------------------------------------------------------ */
export function recMatches(r,date){
  if(date<r.start)return false;
  if(r.skip&&r.skip.includes(date))return false;   // single dates removed from the series
  if(r.until&&date>r.until)return false;
  const iw=isoDow(date);
  if(r.freq==='monthly'){
    if(iw!==isoDow(r.start))return false;
    return nthOfMonth(date)===nthOfMonth(r.start);
  }
  if(!r.days.includes(iw))return false;
  const iv=r.freq==='weekly'?1:r.freq==='biweekly'?2:4;
  const w=Math.floor(diffDays(startOfWeek(r.start),date)/7);
  return w%iv===0;
}
export function occKey(recId,date){return recId+'@'+date}
/* all events in [from,to] inclusive: concrete sessions + virtual occurrences */
export function eventsIn(from,to){
  const out=[]; const concrete=new Set();
  for(const s of S.sessions){
    if(s.date>=from&&s.date<=to){out.push(s)}
    if(s.recId)concrete.add(occKey(s.recId,s.date));
  }
  for(const r of S.recs){
    if(r.ended)continue;
    let d=from<r.start?r.start:from;
    for(;d<=to;d=addDays(d,1)){
      if(recMatches(r,d)&&!concrete.has(occKey(r.id,d))){
        out.push({id:'v_'+occKey(r.id,d),virtual:true,recId:r.id,studentId:r.studentId,date:d,time:r.time,dur:r.dur,status:'planned',meet:r.meet||'',...(r.place?{place:r.place}:{}),...(r.gid?{gid:r.gid,gname:r.gname||''}:{}),...(r.rate!=null&&r.rate!==''?{rate:r.rate}:{})});
      }
    }
  }
  out.sort((a,b)=>(a.date+a.time).localeCompare(b.date+b.time));
  return out;
}
/* turn a virtual occurrence into a concrete session record */
export function materialize(evt){
  if(!evt.virtual)return S.sessions.find(s=>s.id===evt.id)||evt;
  const had=S.sessions.find(s=>s.recId===evt.recId&&s.date===evt.date);if(had)return had;
  const r=S.recs.find(x=>x.id===evt.recId);
  const s={id:uid(),studentId:evt.studentId,date:evt.date,time:evt.time,dur:evt.dur,status:'planned',recId:evt.recId,notes:'',meet:(r&&r.meet)||'',...(r&&r.place?{place:r.place}:{}),...(r&&r.gid?{gid:r.gid,gname:r.gname||''}:{}),...(r&&r.rate!=null&&r.rate!==''?{rate:r.rate}:{})};
  S.sessions.push(s);persist();return s;
}
export function getEvent(id){
  if(id.startsWith('v_')){
    const[recId,date]=id.slice(2).split('@');
    const r=S.recs.find(x=>x.id===recId);
    if(!r)return null;
    return {id,virtual:true,recId,studentId:r.studentId,date,time:r.time,dur:r.dur,status:'planned',meet:r.meet||'',...(r.place?{place:r.place}:{}),...(r.gid?{gid:r.gid,gname:r.gname||''}:{}),...(r.rate!=null&&r.rate!==''?{rate:r.rate}:{})};
  }
  return S.sessions.find(s=>s.id===id)||null;
}
/* ---------- group lessons ----------
   A group lesson is one lesson record (or series) per student, linked by the same gid.
   Billing, recaps, reminders and the parent page keep working per student. */
export function groupMembers(e){
  if(!e||!e.gid)return e?[e]:[];
  return eventsIn(e.date,e.date).filter(x=>x.gid===e.gid&&x.time===e.time);
}
/* one calendar item per group lesson: the first member, with .members */
export function collapseGroups(list){
  const out=[],seen=new Map();
  for(const e of list){
    if(!e.gid){out.push(e);continue}
    const k=e.gid+'|'+e.date+'|'+e.time;
    const g=seen.get(k);
    if(g){g.members.push(e);continue}
    const item={...e,members:[e]};seen.set(k,item);out.push(item);
  }
  for(const g of out)if(g.members&&g.members.length>1){
    const live=g.members.filter(m=>m.status!=='cancelled'&&m.status!=='noshow');
    g.status=live.length?(live.every(m=>m.status==='done')?'done':'planned'):'cancelled';
  }else if(g.members)delete g.members;
  return out;
}
export function groupLabel(members){
  const m=members||[];const n=m[0]&&m[0].gname;
  return n||m.map(x=>(stu(x.studentId)||{}).first||'?').join(', ');
}
/* a lesson of a series moved to another day: its old day must not come back as a planned occurrence */
export function moveOcc(x,date){
  if(x.recId&&x.date&&date&&x.date!==date){const r=S.recs.find(z=>z.id===x.recId);
    if(r){const orig=x.origDate||x.date;r.skip=[...new Set([...(r.skip||[]),orig])].sort();x.origDate=orig}}
}
export function sesEnd(e){return minToHM(hmToMin(e.time)+ +e.dur)}
export function conflicts(date,time,dur,excludeId,gid){
  const a1=hmToMin(time),a2=a1+ +dur;
  return eventsIn(date,date).filter(e=>{
    if(e.id===excludeId||(excludeId&&e.recId&&('v_'+occKey(e.recId,e.date))===excludeId))return false;
    if(gid&&e.gid===gid)return false;   // the other students of the same group lesson
    if(e.status==='cancelled')return false;
    const b1=hmToMin(e.time),b2=b1+ +e.dur;
    return a1<b2&&b1<a2;
  });
}
export function sesAmount(e){const s=stu(e.studentId);if(s&&s._org)return 0;   // a school's lesson isn't the teacher's own revenue
  if(e.pkgId){const p=pkgOf(s,e.pkgId);if(p)return pkgRate(p)*(e.dur/60)}
  const bill=e.status==='done'||((e.status==='cancelled'||e.status==='noshow')&&e.billable);return bill?stuRate(s,e)*(e.dur/60):0}
export function plannedAmount(e){const s=stu(e.studentId);if(s&&s._org)return 0;return stuRate(s,e)*(e.dur/60)}

/* ---------- prepaid lesson packages ----------
   student.packages: [{id,hours,price,date,invoiceId?}]
   Billable lessons on or after a package's date are deducted from it (oldest
   package first, only while the lesson still fits) and get lesson.pkgId; they
   then don't show up as unbilled. A package whose invoice was cancelled or
   deleted stops counting. */
export const pkgOf=(s,id)=>s&&(s.packages||[]).find(p=>p.id===id);
export const pkgRate=p=>p.hours?(+p.price||0)/p.hours:0;
const isBillable=e=>e.status==='done'||((e.status==='cancelled'||e.status==='noshow')&&e.billable);
function pkgActive(p){if(!p.invoiceId)return true;const i=S.invoices.find(x=>x.id===p.invoiceId);return !!i&&i.status!=='cancelled'}
export function pkgUsedMin(p){let m=0;for(const e of S.sessions)if(e.pkgId===p.id)m+=+e.dur||0;return m}
export function pkgLeftMin(p){return Math.max(0,p.hours*60-pkgUsedMin(p))}
/* current package of a student: the oldest active one with hours left, else the newest */
export function pkgCurrent(s){
  const list=(s&&s.packages||[]).filter(pkgActive).sort((a,b)=>a.date.localeCompare(b.date));
  return list.find(p=>pkgLeftMin(p)>0)||list[list.length-1]||null;
}
export function settlePackages(){
  let changed=false;
  const byStu={};
  for(const s of S.students){if(s._org||!s.packages||!s.packages.length)continue;byStu[s.id]=s.packages.filter(pkgActive).sort((a,b)=>a.date.localeCompare(b.date))}
  for(const e of S.sessions){            // drop links that no longer hold
    if(!e.pkgId)continue;
    const list=byStu[e.studentId];
    if(!list||!list.some(p=>p.id===e.pkgId)||!isBillable(e)||e.invoiceId){delete e.pkgId;changed=true}
  }
  for(const id in byStu){
    const list=byStu[id];if(!list.length)continue;
    const left={};for(const p of list)left[p.id]=pkgLeftMin(p);
    const cand=S.sessions.filter(e=>e.studentId===id&&!e.pkgId&&!e.invoiceId&&isBillable(e)).sort((a,b)=>(a.date+a.time).localeCompare(b.date+b.time));
    for(const e of cand){
      const p=list.find(p=>p.date<=e.date&&left[p.id]>=(+e.dur||0));
      if(p){e.pkgId=p.id;left[p.id]-=+e.dur||0;changed=true}
    }
  }
  if(changed)persist();
  return changed;
}

/* ---------- aggregates ---------- */
export function doneSessions(){return S.sessions.filter(s=>s.status==='done'||((s.status==='cancelled'||s.status==='noshow')&&s.billable))}
export function statsRange(from,to){
  let mins=0,amt=0,count=0;
  for(const s of S.sessions){if(s.date>=from&&s.date<=to&&s.status==='done'){mins+=+s.dur;amt+=sesAmount(s);count++}
    else if(s.date>=from&&s.date<=to&&(s.status==='cancelled'||s.status==='noshow')&&s.billable){amt+=sesAmount(s)}}
  return{mins,amt,count};
}
export function unbilledFor(stuId){return S.sessions.filter(s=>s.studentId===stuId&&!s.invoiceId&&!s.pkgId&&(s.status==='done'||((s.status==='cancelled'||s.status==='noshow')&&s.billable)))}
export function outstandingTotal(){return S.invoices.filter(i=>i.status==='sent'||invIsOverdue(i)).reduce((a,i)=>a+i.total,0)}
export function invIsOverdue(i){return i.status!=='paid'&&i.status!=='draft'&&i.status!=='cancelled'&&i.due<today()}
/* a draft dated in the future (e.g. a package that starts next month) is 'scheduled' until that day */
export function invStatus(i){return invIsOverdue(i)?'overdue':i.status==='draft'&&i.issue>today()?'scheduled':i.status}
export function stuTotals(id){
  let mins=0,earned=0,count=0,last=null,unb=0,unbMins=0;
  for(const s of S.sessions){if(s.studentId!==id)continue;
    if(s.status==='done'){mins+=+s.dur;count++;if(!last||s.date>last)last=s.date}
    const a=sesAmount(s);earned+=a;
    if(a&&!s.invoiceId&&!s.pkgId){unb+=a;unbMins+=(s.status==='done'?+s.dur:0)}
  }
  const open=S.invoices.filter(i=>i.studentId===id&&i.status!=='paid'&&i.status!=='draft').reduce((a,i)=>a+i.total,0);
  return{mins,earned,count,last,unb,unbMins,open};
}
export function stuStreak(id){
  let streak=0,w=startOfWeek(today());
  for(let i=0;i<104;i++){
    const from=addDays(w,-7*i),to=addDays(from,6);
    const has=S.sessions.some(s=>s.studentId===id&&s.status==='done'&&s.date>=from&&s.date<=to);
    if(has)streak++;else if(i>0)break;else if(!has&&i===0){/* current week may not have one yet */}
    if(i===0&&!has)continue;
    if(!has)break;
  }
  return streak;
}

/* ---------- templates ---------- */
